import { useState, useCallback } from "react";
import { useGomakeAxios, useSnackBar } from "@/hooks";
import * as XLSX from "xlsx";

interface UseAddProductWithAIOptions {
  quoteId?: string;
  ensureQuoteId?: () => Promise<string | null | undefined>;
  onSuccess?: () => void;
}

type BatchSummary = {
  total: number;
  success: number;
  failed: number;
  created?: number;
};

const emptySummary = (total = 0): BatchSummary => ({
  total,
  success: 0,
  failed: total,
  created: 0,
});

const extractActualResponse = (response: any) => {
  if (response?.data?.data?.success !== undefined) {
    return response.data.data;
  }
  if (response?.data?.success !== undefined) {
    return response.data;
  }
  if (response?.success !== undefined) {
    return response;
  }
  if (response?.data?.data?.data?.success !== undefined) {
    return response.data.data.data;
  }
  return null;
};

const isBatchBody = (candidate: any) =>
  !!candidate &&
  typeof candidate === "object" &&
  (candidate.accepted === true ||
    Array.isArray(candidate.created) ||
    Array.isArray(candidate.failedItems) ||
    !!candidate.summary);

const isJobsExcelAcceptedBody = (candidate: any) =>
  !!candidate &&
  typeof candidate === "object" &&
  (candidate.accepted === true ||
    candidate.phase === "accepted" ||
    (candidate.summary &&
      (Array.isArray(candidate.results) ||
        candidate.phase === "orchestrated")));

const unwrapJobsExcelResponse = (response: any) => {
  const candidates = [
    response?.data?.data?.data,
    response?.data?.data,
    response?.data,
    response,
  ];
  return candidates.find(isJobsExcelAcceptedBody) || null;
};

const unwrapBatchResponse = (response: any) => {
  const candidates = [
    response?.data?.data?.data,
    response?.data?.data,
    response?.data,
    response,
  ];
  return candidates.find(isBatchBody) || null;
};

export const useAddProductWithAI = ({
  quoteId,
  ensureQuoteId,
  onSuccess,
}: UseAddProductWithAIOptions) => {
  const [prompt, setPrompt] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedFileName, setSelectedFileName] = useState<string>("");
  const [parametersFile, setParametersFile] = useState<File | null>(null);
  const [selectedParametersFileName, setSelectedParametersFileName] =
    useState<string>("");
  const [batchFile, setBatchFile] = useState<File | null>(null);
  const [selectedBatchFileName, setSelectedBatchFileName] = useState<string>("");
  const { callApi } = useGomakeAxios();
  const { alertFault, alertSuccess } = useSnackBar();

  const fileToBase64 = useCallback((file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result as string;
        const base64 = dataUrl.includes(",")
          ? dataUrl.split(",")[1]!
          : dataUrl;
        resolve(base64);
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
  }, []);

  const clearOtherInputs = useCallback(
    (keep: "prompt" | "excel" | "parameters" | "batch") => {
      if (keep !== "prompt") {
        setPrompt("");
      }
      if (keep !== "excel") {
        setSelectedFile(null);
        setSelectedFileName("");
      }
      if (keep !== "parameters") {
        setParametersFile(null);
        setSelectedParametersFileName("");
      }
      if (keep !== "batch") {
        setBatchFile(null);
        setSelectedBatchFileName("");
      }
    },
    []
  );

  const handleFileSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        setSelectedFile(file);
        setSelectedFileName(file.name);
        clearOtherInputs("excel");
      }
    },
    [clearOtherInputs]
  );

  const handleParametersFileSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        setParametersFile(file);
        setSelectedParametersFileName(file.name);
        clearOtherInputs("parameters");
      }
    },
    [clearOtherInputs]
  );

  const resolveQuoteId = useCallback(async () => {
    if (quoteId) {
      return quoteId;
    }

    if (ensureQuoteId) {
      return await ensureQuoteId();
    }

    return undefined;
  }, [quoteId, ensureQuoteId]);

  const handleBatchFileSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        setBatchFile(file);
        setSelectedBatchFileName(file.name);
        clearOtherInputs("batch");
        // Create quote immediately when batch file is selected (if needed).
        void resolveQuoteId();
      }
    },
    [clearOtherInputs, resolveQuoteId]
  );

  // Also warm quote id when Job Basket file is chosen (async navigate path).
  const handleJobsFileSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      handleFileSelect(event);
      void resolveQuoteId();
    },
    [handleFileSelect, resolveQuoteId]
  );

  const processExcelFile = useCallback(async (file: File) => {
    return new Promise<string[]>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: "array" });
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];

          const jsonData = XLSX.utils.sheet_to_json(firstSheet, {
            header: 1,
            defval: "",
            raw: false,
            blankrows: false,
          }) as any[][];

          const prompts: string[] = [];

          for (let i = 0; i < jsonData.length; i++) {
            const row = jsonData[i];

            if (!row || !Array.isArray(row) || row.length === 0) {
              continue;
            }

            const firstCell = row[0];
            const cellValue =
              firstCell !== null && firstCell !== undefined
                ? String(firstCell).trim()
                : "";

            if (cellValue.length > 0) {
              prompts.push(cellValue);
            }
          }

          if (prompts.length === 0) {
            reject(
              new Error(
                "No valid prompts found in Excel file. Please ensure the first column contains data."
              )
            );
            return;
          }

          resolve(prompts);
        } catch (error) {
          console.error("Error processing Excel file:", error);
          reject(error);
        }
      };
      reader.onerror = (error) => {
        console.error("FileReader error:", error);
        reject(error);
      };
      reader.readAsArrayBuffer(file);
    });
  }, []);

  const sendPromptToAI = useCallback(
    async (promptText: string) => {
      try {
        const resolvedQuoteId = await resolveQuoteId();

        if (!resolvedQuoteId) {
          alertFault("No quote in create was found");
          return false;
        }

        const response = await callApi(
          "POST",
          "/v1/gomake-ai-service/quote-cart/add-product-with-ai",
          {
            quoteId: resolvedQuoteId,
            prompt: promptText,
          },
          false
        );

        if (response?.success) {
          return true;
        } else {
          alertFault(response?.message || "Failed to process AI request");
          return false;
        }
      } catch (error: any) {
        console.error("Error sending prompt to AI:", error);
        alertFault(error?.message || "An error occurred");
        return false;
      }
    },
    [resolveQuoteId, callApi, alertFault]
  );

  const sendPromptsBatchToAI = useCallback(
    async (prompts: string[]) => {
      try {
        const resolvedQuoteId = await resolveQuoteId();

        if (!resolvedQuoteId) {
          alertFault("No quote in create was found");
          return {
            success: false,
            summary: emptySummary(prompts.length),
            results: [],
            failedItems: [],
          };
        }

        const response = await callApi(
          "POST",
          "/v1/gomake-ai-service/quote-cart/add-products-with-ai-batch",
          {
            quoteId: resolvedQuoteId,
            prompts,
          },
          false
        );

        const actualResponse = extractActualResponse(response);
        const isHttpSuccess =
          response?.success === true || response?.status === 200;

        if (actualResponse?.success === true && actualResponse?.summary) {
          return {
            success: true,
            summary: actualResponse.summary,
            results: actualResponse.results || [],
            failedItems: actualResponse.failedItems || [],
          };
        } else if (isHttpSuccess) {
          return {
            success: true,
            summary: {
              total: prompts.length,
              success: prompts.length,
              failed: 0,
            },
            results: prompts.map((p: string) => ({
              prompt: p,
              success: true,
              sessionId: null,
              message: "Processing...",
            })),
            failedItems: [],
          };
        } else {
          alertFault(
            actualResponse?.message ||
              response?.message ||
              "Failed to process batch AI request"
          );
          return {
            success: false,
            summary: emptySummary(prompts.length),
            results: [],
            failedItems: [],
          };
        }
      } catch (error: any) {
        console.error("Error sending batch prompts to AI:", error);
        alertFault(
          error?.message || "An error occurred during batch processing"
        );
        return {
          success: false,
          summary: emptySummary(prompts.length),
          results: [],
          failedItems: [],
        };
      }
    },
    [resolveQuoteId, callApi, alertFault]
  );

  const sendJobsExcelToAI = useCallback(
    async (file: File) => {
      try {
        const resolvedQuoteId = await resolveQuoteId();

        if (!resolvedQuoteId) {
          alertFault("No quote in create was found");
          return {
            success: false,
            accepted: false,
            summary: emptySummary(),
            results: [],
            failedItems: [],
          };
        }

        const fileBase64 = await fileToBase64(file);
        const response = await callApi(
          "POST",
          "/v1/gomake-ai-service/quote-cart/upload-jobs-excel",
          {
            quoteId: resolvedQuoteId,
            fileBase64,
            fileName: file.name,
          },
          false
        );

        const jobsBody = unwrapJobsExcelResponse(response);
        const responseAny = response as {
          success?: boolean;
          status?: number;
          message?: string;
          accepted?: boolean;
          data?: { accepted?: boolean; message?: string };
        };
        const isHttpSuccess =
          responseAny?.success === true || responseAny?.status === 200;
        const isAccepted =
          jobsBody?.accepted === true ||
          responseAny?.data?.accepted === true ||
          responseAny?.accepted === true ||
          jobsBody?.phase === "accepted";

        // Async accept: placeholders added, Agent 2/3 continue in background
        if (isHttpSuccess && (isAccepted || jobsBody?.summary)) {
          return {
            success: true,
            accepted: true,
            summary: jobsBody?.summary || {
              total: 0,
              success: 0,
              failed: 0,
              created: 0,
            },
            results: jobsBody?.results || [],
            failedItems: (jobsBody?.results || []).filter(
              (row: { success?: boolean }) => row?.success === false
            ),
            message:
              jobsBody?.message ||
              response?.message ||
              "Job Basket uploaded. Quote items are being calculated...",
          };
        }

        const actualResponse = extractActualResponse(response);

        if (
          actualResponse?.success === true &&
          (actualResponse?.accepted === true || actualResponse?.summary)
        ) {
          return {
            success: true,
            accepted: actualResponse?.accepted === true,
            summary: actualResponse.summary || emptySummary(),
            results: actualResponse.results || [],
            failedItems: actualResponse.failedItems || [],
            message: actualResponse.message,
          };
        }

        alertFault(
          jobsBody?.message ||
            actualResponse?.message ||
            response?.message ||
            "Failed to process Job Basket Excel file"
        );
        return {
          success: false,
          accepted: false,
          summary: emptySummary(),
          results: [],
          failedItems: [],
        };
      } catch (error: unknown) {
        const err = error as { message?: string };
        alertFault(err?.message || "An error occurred");
        return {
          success: false,
          accepted: false,
          summary: emptySummary(),
          results: [],
          failedItems: [],
        };
      }
    },
    [resolveQuoteId, callApi, alertFault, fileToBase64]
  );

  const sendParametersFileToAI = useCallback(
    async (file: File) => {
      try {
        const resolvedQuoteId = await resolveQuoteId();

        if (!resolvedQuoteId) {
          alertFault("No quote in create was found");
          return {
            success: false,
            summary: emptySummary(),
            results: [] as { job?: Record<string, string>; success: boolean }[],
            failedItems: [],
          };
        }

        const fileBase64 = await fileToBase64(file);
        const response = await callApi(
          "POST",
          "/v1/gomake-ai-service/quote-cart/add-products-from-parameters-file",
          {
            quoteId: resolvedQuoteId,
            fileBase64,
            fileName: file.name,
          },
          false
        );

        const actualResponse = extractActualResponse(response);
        const isHttpSuccess = response?.success === true;

        if (actualResponse?.success === true && actualResponse?.summary) {
          return {
            success: true,
            summary: actualResponse.summary,
            results: actualResponse.results || [],
            failedItems: actualResponse.failedItems || [],
          };
        }
        if (isHttpSuccess && !actualResponse) {
          return {
            success: true,
            summary: { total: 1, success: 1, failed: 0 },
            results: [] as {
              job?: Record<string, string>;
              success: boolean;
            }[],
            failedItems: [],
          };
        }
        alertFault(
          actualResponse?.message ||
            response?.message ||
            "Failed to process parameters file"
        );
        return {
          success: false,
          summary: emptySummary(),
          results: [] as { job?: Record<string, string>; success: boolean }[],
          failedItems: [],
        };
      } catch (error: unknown) {
        const err = error as { message?: string };
        alertFault(err?.message || "An error occurred");
        return {
          success: false,
          summary: emptySummary(),
          results: [] as { job?: Record<string, string>; success: boolean }[],
          failedItems: [],
        };
      }
    },
    [resolveQuoteId, callApi, alertFault, fileToBase64]
  );

  const sendBatchFileToAI = useCallback(
    async (file: File) => {
      try {
        const resolvedQuoteId = await resolveQuoteId();

        if (!resolvedQuoteId) {
          alertFault("No quote in create was found");
          return {
            success: false,
            summary: emptySummary(),
            created: [] as { productName?: string; quoteItemId?: string }[],
            failedItems: [] as {
              productName?: string;
              message?: string;
            }[],
          };
        }

        const fileBase64 = await fileToBase64(file);
        const response = await callApi(
          "POST",
          "/v1/gomake-ai-service/quote-cart/add-products-from-batch-file",
          {
            quoteId: resolvedQuoteId,
            fileBase64,
            fileName: file.name,
            mimeType: file.type || undefined,
          },
          false
        );

        const batchBody = unwrapBatchResponse(response);
        const isHttpSuccess =
          response?.success === true || response?.status === 200;

        // Backend accepts immediately; extract/ERP continue in background.
        if (isHttpSuccess || batchBody?.accepted === true || batchBody?.success === true) {
          return {
            success: true,
            accepted: true,
            summary: batchBody?.summary || {
              total: 0,
              success: 1,
              created: 0,
              failed: 0,
            },
            created: batchBody?.created || [],
            failedItems: batchBody?.failedItems || [],
            message:
              batchBody?.message ||
              response?.message ||
              "Batch file uploaded. Quote items are being analyzed...",
          };
        }

        alertFault(response?.message || "Failed to process batch file");
        return {
          success: false,
          summary: emptySummary(),
          created: [],
          failedItems: [],
          message: response?.message,
        };
      } catch (error: unknown) {
        const err = error as { message?: string };
        alertFault(err?.message || "An error occurred");
        return {
          success: false,
          summary: emptySummary(),
          created: [],
          failedItems: [],
        };
      }
    },
    [resolveQuoteId, callApi, alertFault, fileToBase64]
  );

  const handleSubmitPrompt = useCallback(async () => {
    if (!prompt.trim() && !selectedFile && !parametersFile && !batchFile) {
      alertFault(
        "Please enter a prompt, select an Excel file, a parameters file, or a batch file"
      );
      return false;
    }

    setIsProcessing(true);
    let hasSuccess = false;

    try {
      if (batchFile) {
        // Batch UX is handled by the modal (dismiss immediately + analyzing dialog).
        // Keep this path as a fallback if called directly.
        const batchResult = await sendBatchFileToAI(batchFile);
        if (batchResult.success) {
          hasSuccess = true;
        }
      } else if (parametersFile) {
        const batchResult = await sendParametersFileToAI(parametersFile);

        if (batchResult.success) {
          const { summary } = batchResult;
          const successCount = summary.success || 0;
          const failCount = summary.failed || 0;

          if (successCount > 0) {
            alertSuccess(
              `Successfully started ${successCount} job${
                successCount > 1 ? "s" : ""
              } from parameters${failCount > 0 ? ` (${failCount} failed)` : ""}.`
            );
            hasSuccess = true;
          } else {
            alertFault("Failed to process any jobs from the parameters file");
          }
        } else {
          alertFault("Failed to process parameters file");
        }
      } else if (selectedFile) {
        const jobsResult = await sendJobsExcelToAI(selectedFile);

        if (jobsResult.success) {
          // Async accept: placeholders are on the quote; Agent 2/3 run in background
          if (jobsResult.accepted) {
            hasSuccess = true;
          } else {
            const { summary } = jobsResult;
            const successCount =
              summary.erpSuccess ?? summary.success ?? 0;
            const failCount = summary.erpFailed ?? summary.failed ?? 0;
            const total =
              summary.total ?? summary.rowCount ?? successCount + failCount;

            if (total > 0) {
              if (successCount > 0) {
                alertSuccess(
                  `Successfully processed ${successCount} Job Basket row${
                    successCount > 1 ? "s" : ""
                  }${
                    failCount > 0 ? ` (${failCount} failed)` : ""
                  }. Products are being calculated...`
                );
              } else {
                const firstFailed = jobsResult.results?.find(
                  (row: { success?: boolean; message?: string }) =>
                    row?.success === false
                );
                alertFault(
                  firstFailed?.message ||
                    jobsResult.message ||
                    `Job Basket uploaded but all ${total} row(s) failed ERP validation.`
                );
              }
              hasSuccess = true;
            } else {
              alertFault("Failed to process any rows from Job Basket Excel file");
            }
          }
        } else {
          alertFault("Failed to process Job Basket Excel file");
        }
      } else {
        const success = await sendPromptToAI(prompt.trim());
        if (success) {
          alertSuccess(
            "Product added to quote cart. Calculation in progress..."
          );
          hasSuccess = true;
        }
      }
    } catch (error: any) {
      console.error("Error processing request:", error);
      alertFault(error?.message || "An error occurred");
      return false;
    } finally {
      setIsProcessing(false);
      setPrompt("");
      setSelectedFile(null);
      setSelectedFileName("");
      setParametersFile(null);
      setSelectedParametersFileName("");
      setBatchFile(null);
      setSelectedBatchFileName("");
    }
    return hasSuccess;
  }, [
    prompt,
    selectedFile,
    parametersFile,
    batchFile,
    sendPromptToAI,
    sendJobsExcelToAI,
    sendParametersFileToAI,
    sendBatchFileToAI,
    onSuccess,
    alertFault,
    alertSuccess,
  ]);

  const handleFileUpload = useCallback(() => {
    handleSubmitPrompt();
  }, [handleSubmitPrompt]);

  return {
    prompt,
    setPrompt,
    isProcessing,
    handleSubmitPrompt,
    handleFileUpload,
    handleFileSelect,
    handleJobsFileSelect,
    handleParametersFileSelect,
    handleBatchFileSelect,
    selectedFileName,
    selectedParametersFileName,
    selectedBatchFileName,
    selectedFile,
    batchFile,
    resolveQuoteId,
    sendBatchFileToAI,
    sendJobsExcelToAI,
    clearBatchSelection: () => {
      setBatchFile(null);
      setSelectedBatchFileName("");
    },
    clearJobsSelection: () => {
      setSelectedFile(null);
      setSelectedFileName("");
    },
  };
};
